/**
 * IntegrationsSettings - Custom API integrations management
 * 
 * Allows architects to define external APIs used by the application:
 * - Name, Root Endpoint, Documentation URL
 * - Description of how the API is used
 * - Auth type, Rate limits
 * - Environment-specific endpoints
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  Plus, 
  Trash2, 
  ExternalLink, 
  Loader2,
  Globe,
  Key,
  Clock,
  ChevronDown,
  ChevronUp,
  Edit2,
  X,
  Check
} from 'lucide-react';
import { deliveryConfigApi } from '../../services/api';
import { toast } from 'sonner';

const IntegrationsSettings = ({ productId, isOwner }) => {
  const [apis, setApis] = useState([]);
  const [options, setOptions] = useState({ auth_types: [], rate_limit_periods: [], environments: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  
  // Form state for new/edit API
  const [formData, setFormData] = useState({
    name: '',
    root_endpoint: '',
    documentation_url: '',
    description: '',
    auth_type: 'api_key',
    rate_limit: { requests: '', period: 'minute' },
    environments: { development: '', staging: '', production: '' }
  });

  // Load integrations and options
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [integrationsRes, optionsRes] = await Promise.all([
          deliveryConfigApi.getIntegrations(productId),
          deliveryConfigApi.getIntegrationOptions(productId)
        ]);
        setApis(integrationsRes.data.custom_apis || []);
        setOptions(optionsRes.data || { auth_types: [], rate_limit_periods: [], environments: [] });
      } catch (err) {
        console.error('Failed to load integrations:', err);
        toast.error('Failed to load API integrations');
      } finally {
        setLoading(false);
      }
    };
    
    if (productId) {
      loadData();
    }
  }, [productId]);

  const resetForm = () => {
    setFormData({
      name: '',
      root_endpoint: '',
      documentation_url: '',
      description: '',
      auth_type: 'api_key',
      rate_limit: { requests: '', period: 'minute' },
      environments: { development: '', staging: '', production: '' }
    });
  };

  const handleAddClick = () => {
    resetForm();
    setShowAddForm(true);
    setEditingId(null);
  };

  const handleEditClick = (api) => {
    setFormData({
      name: api.name || '',
      root_endpoint: api.root_endpoint || '',
      documentation_url: api.documentation_url || '',
      description: api.description || '',
      auth_type: api.auth_type || 'api_key',
      rate_limit: {
        requests: api.rate_limit?.requests || '',
        period: api.rate_limit?.period || 'minute'
      },
      environments: {
        development: api.environments?.development || '',
        staging: api.environments?.staging || '',
        production: api.environments?.production || ''
      }
    });
    setEditingId(api.api_id);
    setShowAddForm(false);
    setExpandedId(api.api_id);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    resetForm();
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.root_endpoint.trim()) {
      toast.error('Root endpoint is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        name: formData.name.trim(),
        root_endpoint: formData.root_endpoint.trim(),
        documentation_url: formData.documentation_url.trim() || null,
        description: formData.description.trim(),
        auth_type: formData.auth_type,
        rate_limit: formData.rate_limit.requests ? {
          requests: parseInt(formData.rate_limit.requests),
          period: formData.rate_limit.period
        } : null,
        environments: (formData.environments.development || formData.environments.staging || formData.environments.production) ? {
          development: formData.environments.development || null,
          staging: formData.environments.staging || null,
          production: formData.environments.production || null
        } : null
      };

      if (editingId) {
        // Update existing
        const res = await deliveryConfigApi.updateIntegration(productId, editingId, payload);
        setApis(prev => prev.map(a => a.api_id === editingId ? res.data.api : a));
        toast.success('API integration updated');
      } else {
        // Create new
        const res = await deliveryConfigApi.createIntegration(productId, payload);
        setApis(prev => [...prev, res.data.api]);
        toast.success('API integration added');
      }
      
      handleCancel();
    } catch (err) {
      console.error('Failed to save integration:', err);
      toast.error('Failed to save API integration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (apiId) => {
    if (!window.confirm('Are you sure you want to delete this API integration?')) {
      return;
    }

    try {
      setSaving(true);
      await deliveryConfigApi.deleteIntegration(productId, apiId);
      setApis(prev => prev.filter(a => a.api_id !== apiId));
      toast.success('API integration deleted');
    } catch (err) {
      console.error('Failed to delete integration:', err);
      toast.error('Failed to delete API integration');
    } finally {
      setSaving(false);
    }
  };

  const getAuthTypeLabel = (value) => {
    const authType = options.auth_types.find(a => a.value === value);
    return authType?.label || value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="integrations-settings">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="w-4 h-4" />
                External API Integrations
              </CardTitle>
              <CardDescription className="mt-1">
                Define third-party APIs your application consumes. This context helps AI assistants understand your integration dependencies.
              </CardDescription>
            </div>
            {isOwner && !showAddForm && !editingId && (
              <Button 
                onClick={handleAddClick}
                size="sm"
                className="gap-1"
                data-testid="add-integration-btn"
              >
                <Plus className="w-4 h-4" />
                Add API
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Add New Form */}
      {showAddForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Add New API Integration</CardTitle>
          </CardHeader>
          <CardContent>
            <ApiForm
              formData={formData}
              setFormData={setFormData}
              options={options}
              saving={saving}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </CardContent>
        </Card>
      )}

      {/* API List */}
      {apis.length === 0 && !showAddForm ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center text-gray-500">
            <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No API integrations configured yet.</p>
            {isOwner && (
              <p className="text-sm mt-1">Click &quot;Add API&quot; to document your external dependencies.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {apis.map((api) => (
            <Card key={api.api_id} className={editingId === api.api_id ? 'border-blue-200' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === api.api_id ? null : api.api_id)}
                  >
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base">{api.name}</CardTitle>
                      <Badge variant="outline" className="text-xs">
                        {getAuthTypeLabel(api.auth_type)}
                      </Badge>
                      {api.rate_limit?.requests && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <Clock className="w-3 h-3" />
                          {api.rate_limit.requests}/{api.rate_limit.period}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                        {api.root_endpoint}
                      </code>
                      {api.documentation_url && (
                        <a 
                          href={api.documentation_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isOwner && editingId !== api.api_id && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditClick(api)}
                          data-testid={`edit-api-${api.api_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(api.api_id)}
                          data-testid={`delete-api-${api.api_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setExpandedId(expandedId === api.api_id ? null : api.api_id)}
                    >
                      {expandedId === api.api_id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {expandedId === api.api_id && (
                <CardContent className="pt-0">
                  {editingId === api.api_id ? (
                    <ApiForm
                      formData={formData}
                      setFormData={setFormData}
                      options={options}
                      saving={saving}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  ) : (
                    <div className="space-y-3 pt-2 border-t">
                      {/* Description */}
                      <div>
                        <Label className="text-xs text-gray-500">How it&apos;s used</Label>
                        <p className="text-sm mt-1">{api.description}</p>
                      </div>
                      
                      {/* Environment Endpoints */}
                      {(api.environments?.development || api.environments?.staging || api.environments?.production) && (
                        <div>
                          <Label className="text-xs text-gray-500">Environment Endpoints</Label>
                          <div className="grid grid-cols-3 gap-2 mt-1">
                            {['development', 'staging', 'production'].map((env) => (
                              api.environments?.[env] && (
                                <div key={env} className="text-xs">
                                  <span className="text-gray-500 capitalize">{env}:</span>
                                  <code className="block bg-gray-100 px-1 py-0.5 rounded truncate">
                                    {api.environments[env]}
                                  </code>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Reusable form component for create/edit
const ApiForm = ({ formData, setFormData, options, saving, onSave, onCancel }) => {
  return (
    <div className="space-y-4">
      {/* Row 1: Name and Root Endpoint */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="api-name">Name <span className="text-red-500">*</span></Label>
          <Input
            id="api-name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Stripe Payments"
            data-testid="api-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="api-endpoint">Root Endpoint <span className="text-red-500">*</span></Label>
          <Input
            id="api-endpoint"
            value={formData.root_endpoint}
            onChange={(e) => setFormData(prev => ({ ...prev, root_endpoint: e.target.value }))}
            placeholder="https://api.stripe.com/v1"
            data-testid="api-endpoint-input"
          />
        </div>
      </div>

      {/* Row 2: Documentation URL */}
      <div className="space-y-2">
        <Label htmlFor="api-docs">Documentation URL</Label>
        <Input
          id="api-docs"
          value={formData.documentation_url}
          onChange={(e) => setFormData(prev => ({ ...prev, documentation_url: e.target.value }))}
          placeholder="https://stripe.com/docs/api"
          data-testid="api-docs-input"
        />
      </div>

      {/* Row 3: Description */}
      <div className="space-y-2">
        <Label htmlFor="api-description">Description <span className="text-red-500">*</span></Label>
        <Textarea
          id="api-description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe how this API is used in your application..."
          rows={3}
          data-testid="api-description-input"
        />
      </div>

      {/* Row 4: Auth Type and Rate Limit */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Authentication</Label>
          <Select
            value={formData.auth_type}
            onValueChange={(v) => setFormData(prev => ({ ...prev, auth_type: v }))}
          >
            <SelectTrigger data-testid="api-auth-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.auth_types.map((type) => (
                <SelectItem key={type.value} value={type.value} className="py-2">
                  <div className="flex flex-col">
                    <span>{type.label}</span>
                    <span className="text-xs text-gray-500">{type.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="rate-requests">Rate Limit (requests)</Label>
          <Input
            id="rate-requests"
            type="number"
            value={formData.rate_limit.requests}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              rate_limit: { ...prev.rate_limit, requests: e.target.value }
            }))}
            placeholder="100"
            data-testid="api-rate-requests-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Rate Period</Label>
          <Select
            value={formData.rate_limit.period}
            onValueChange={(v) => setFormData(prev => ({
              ...prev,
              rate_limit: { ...prev.rate_limit, period: v }
            }))}
          >
            <SelectTrigger data-testid="api-rate-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.rate_limit_periods.map((period) => (
                <SelectItem key={period.value} value={period.value}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 5: Environment-specific endpoints */}
      <div className="space-y-2">
        <Label className="text-sm text-gray-600">Environment-specific Endpoints (optional)</Label>
        <div className="grid grid-cols-3 gap-4">
          {['development', 'staging', 'production'].map((env) => (
            <div key={env} className="space-y-1">
              <Label className="text-xs text-gray-500 capitalize">{env}</Label>
              <Input
                value={formData.environments[env]}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  environments: { ...prev.environments, [env]: e.target.value }
                }))}
                placeholder={`${env} endpoint`}
                className="text-sm"
                data-testid={`api-env-${env}-input`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button 
          variant="outline" 
          onClick={onCancel}
          disabled={saving}
        >
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
        <Button 
          onClick={onSave}
          disabled={saving}
          data-testid="save-api-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-1" />
          )}
          Save
        </Button>
      </div>
    </div>
  );
};

export default IntegrationsSettings;
